import React, { useState, useEffect, useCallback } from 'react';
import { WifiOff } from 'lucide-react';
import { addSubscriber } from '../firebase/services';
import { getStorefrontSettings } from '../firebase/storefront';
import { useToast } from '../components/Toast';
import { useOffline } from '../hooks/useOffline';
import { safeAsync } from '../utils/errorHandler';

// Import extracted components
import { Navigation } from '../components/Navigation';
import { Hero } from '../components/Hero';
import { About } from '../components/About';
import { Programme } from '../components/Programme';
import { Freebie } from '../components/Freebie';
import { Booking } from '../components/Booking';
import { Footer } from '../components/Footer';

export function LandingPage({ onNavigateToAdmin, onNavigateToThankYou }) {
    const [storefrontSettings, setStorefrontSettings] = useState(null);
    const [settingsLoading, setSettingsLoading] = useState(true);
    const { showToast } = useToast();
    const { isOnline } = useOffline();

    // Load storefront settings for hero image - load immediately on mount
    useEffect(() => {
        let isMounted = true;

        const loadSettings = async () => {
            if (!isMounted) return;
            setSettingsLoading(true);
            try {
                const settings = await getStorefrontSettings();
                if (isMounted) {
                    setStorefrontSettings(settings);
                }
            } catch (error) {
                if (import.meta.env.DEV) {
                    console.error('Error loading storefront settings:', error);
                }
            } finally {
                if (isMounted) {
                    setSettingsLoading(false);
                }
            }
        };
        loadSettings();

        return () => {
            isMounted = false;
        };
    }, []);

    // Show offline indicator
    useEffect(() => {
        if (!isOnline) {
            showToast('You are offline. Some features may not work.', 'warning', 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOnline]);

    const handleScroll = useCallback((id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);

    // Extract form submission handler - only depends on stable values
    const handleFreebieSubmit = useCallback(async (name, email) => {
        console.log('=== FORM SUBMISSION STARTED ===');
        console.log('Name:', name);
        console.log('Email:', email);

        // Validation
        if (!name.trim()) {
            console.log('Validation failed: Name is empty');
            showToast('Please enter your name', 'error');
            return false;
        }

        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            console.log('Validation failed: Email is invalid');
            showToast('Please enter a valid email address', 'error');
            return false;
        }

        if (!isOnline) {
            console.log('Validation failed: User is offline');
            showToast('You are offline. Please check your connection.', 'error');
            return false;
        }

        console.log('Validation passed, calling addSubscriber...');
        try {
            // Save to Firebase Firestore with error handling
            const result = await safeAsync(
                () => {
                    console.log('Inside safeAsync, calling addSubscriber...');
                    return addSubscriber(name.trim(), email.trim());
                },
                { success: false, error: 'Network error' }
            );

            console.log('addSubscriber result:', result);

            if (result && result.success) {
                console.log('Subscriber added successfully!');
                showToast('Success! Check your email for the meditation', 'success');
                // Small delay for better UX
                setTimeout(() => {
                    onNavigateToThankYou?.();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 500);
                return true;
            } else {
                console.error('addSubscriber failed:', result?.error);
                showToast(result?.error || 'Something went wrong. Please try again.', 'error');
                return false;
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            console.error('Error stack:', error.stack);
            showToast('Network error. Please check your connection and try again.', 'error');
            return false;
        }
    }, [isOnline, showToast, onNavigateToThankYou]);

    return (
        <div className="font-sans text-gray-800 antialiased selection:bg-[#fae1e5] selection:text-[#d48c95]">
            {/* Offline Indicator */}
            {!isOnline && (
                <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-white px-4 py-2 text-sm text-center z-[100] flex items-center justify-center gap-2">
                    <WifiOff size={16} />
                    <span>You are offline. Some features may not work.</span>
                </div>
            )}

            <Navigation
                onScrollTo={handleScroll}
                onAdminClick={onNavigateToAdmin}
                onHomeClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            />
            <Hero
                storefrontSettings={storefrontSettings}
                settingsLoading={settingsLoading}
                onScrollTo={handleScroll}
            />
            <About storefrontSettings={storefrontSettings} />
            <Programme
                storefrontSettings={storefrontSettings}
                onScrollTo={handleScroll}
            />
            <Freebie
                storefrontSettings={storefrontSettings}
                onSubmit={handleFreebieSubmit}
            />
            <Booking onScrollTo={handleScroll} storefrontSettings={storefrontSettings} />
            <Footer />
        </div>
    );
}

export default LandingPage;
